import React, { useState, useRef, useCallback } from 'react';
import { CONSTANTS } from '../data.js';
import { combinePair } from '../lib/utils';
import type { DragState, PaletteDrag, Instance, Effect, TutorialState } from '../types';

interface UseBoardInteractionsProps {
  instances: Instance[];
  updateInstancePos: (id: number, x: number, y: number) => void;
  removeInstanceById: (id: number) => void;
  tryCombine: (a: Instance, boardRect: DOMRect) => string | 'fail' | null;
  addInstance: (elementKey: string, x: number, y: number) => void;
  setInstances: React.Dispatch<React.SetStateAction<Instance[]>>;
  setEffects: React.Dispatch<React.SetStateAction<Effect[]>>;
  boardRef: React.RefObject<HTMLDivElement | null>;
  libraryRef: React.RefObject<HTMLDivElement | null>;
  playSoundEffect: (key: string) => void;
  gamePhase: string;
  tutorialState: TutorialState;
  advanceTutorial: () => void;
}

export const useBoardInteractions = ({
  instances,
  updateInstancePos,
  removeInstanceById,
  tryCombine,
  addInstance,
  setInstances,
  setEffects,
  boardRef,
  libraryRef,
  playSoundEffect,
  gamePhase,
  tutorialState,
  advanceTutorial,
}: UseBoardInteractionsProps) => {
  const [drag, setDrag] = useState<DragState>(null);
  const [overLibrary, setOverLibrary] = useState<boolean>(false);
  const [highlightedIds, setHighlightedIds] = useState<number[]>([]);
  const [paletteDrag, setPaletteDrag] = useState<PaletteDrag>(null);

  const animationFrameRef = useRef<number | null>(null);
  const lastHighlightUpdateRef = useRef<number>(0);

  const onPointerDown = useCallback((e: React.PointerEvent, id: number) => {
    if (gamePhase !== 'normal') return;
    
    const inst = instances.find(i => i.id === id);
    if (!inst || !boardRef.current) return;
    
    const rect = boardRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    setDrag({
      id,
      offsetX: mouseX - inst.x,
      offsetY: mouseY - inst.y,
      isDragging: false,
      startTime: performance.now()
    });
    
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, [instances, boardRef, gamePhase]);

  const updateHighlights = useCallback((draggedId: number, x: number, y: number) => {
    if (gamePhase !== 'normal') return;
    
    const now = performance.now();
    const throttle = CONSTANTS.drag?.highlightUpdateThrottle || 16;
    
    if (now - lastHighlightUpdateRef.current < throttle) return;
    lastHighlightUpdateRef.current = now;

    let candidate: Instance | null = null;
    let minD2 = Infinity;
    const threshold2 = (CONSTANTS.drag?.threshold || 48) ** 2;

    for (const it of instances) {
      if (it.id === draggedId) continue;
      const dx = (it.x + 32) - (x + 32);
      const dy = (it.y + 32) - (y + 32);
      const d2 = dx * dx + dy * dy;
      if (d2 < minD2) {
        minD2 = d2;
        candidate = it;
      }
    }

    if (candidate && minD2 < threshold2) {
      setHighlightedIds([draggedId, candidate.id]);
    } else {
      setHighlightedIds([]);
    }
  }, [instances, gamePhase]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!drag || !boardRef.current || gamePhase !== 'normal') return;
    
    const rect = boardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - drag.offsetX;
    const y = e.clientY - rect.top - drag.offsetY;
    
    const now = performance.now();
    const dragDelay = CONSTANTS.drag?.dragStartDelay || 50;
    if (!drag.isDragging && (now - drag.startTime) > dragDelay) {
      setDrag(prev => prev ? { ...prev, isDragging: true } : null);
    }
    
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    
    animationFrameRef.current = requestAnimationFrame(() => {
      updateInstancePos(drag.id, x, y);
      if (drag.isDragging) {
        updateHighlights(drag.id, x, y);
      }
    });

    const libRect = libraryRef.current?.getBoundingClientRect();
    if (libRect) {
      setOverLibrary(
        e.clientX >= libRect.left &&
        e.clientX <= libRect.right &&
        e.clientY >= libRect.top &&
        e.clientY <= libRect.bottom
      );
    } else {
      setOverLibrary(false);
    }
  }, [drag, boardRef, gamePhase, updateInstancePos, updateHighlights, libraryRef]);

  const onPointerUp = useCallback(() => {
    if (!drag || gamePhase !== 'normal') return;

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (overLibrary) {
      removeInstanceById(drag.id);
    } else if (drag.isDragging) {
      const draggedInstance = instances.find(i => i.id === drag.id);
      if (draggedInstance && boardRef.current) {
        const rect = boardRef.current.getBoundingClientRect();
        const clampedX = Math.min(Math.max(draggedInstance.x, 8), rect.width - 72);
        const clampedY = Math.min(Math.max(draggedInstance.y, 64), rect.height - 72);
        
        updateInstancePos(drag.id, clampedX, clampedY);
        
        const finalInstance = { ...draggedInstance, x: clampedX, y: clampedY };
        const combinationResult = tryCombine(finalInstance, rect);

        if (combinationResult && combinationResult !== 'fail') {
          playSoundEffect('combine_success');
        } else if (combinationResult === 'fail') {
          playSoundEffect('combine_fail');
        }
      }
    }
    
    setDrag(null);
    setHighlightedIds([]);
    setOverLibrary(false);
  }, [drag, gamePhase, overLibrary, removeInstanceById, instances, boardRef, updateInstancePos, tryCombine, playSoundEffect]);

  const onPointerCancel = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    setDrag(null);
    setHighlightedIds([]);
    setOverLibrary(false);
  }, []);

  const getBoardCoords = useCallback((clientX: number, clientY: number) => {
    const rect = boardRef.current?.getBoundingClientRect();
    const x = rect ? clientX - rect.left : clientX;
    const y = rect ? clientY - rect.top : clientY;
    return { x, y, rect };
  }, [boardRef]);

  const onBoardDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (gamePhase !== 'normal') return;
    
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';

    const elementKey = e.dataTransfer.getData('text/plain');
    if (!elementKey) {
      setHighlightedIds([]);
      setPaletteDrag(null);
      return;
    }
    const { x, y } = getBoardCoords(e.clientX, e.clientY);

    const overlayX = x - 32;
    const overlayY = y - 32;

    let target: Instance | null = null;
    let minD2 = Infinity;
    for (const it of instances) {
      const dx = (it.x + 32) - x;
      const dy = (it.y + 32) - y;
      const d2 = dx * dx + dy * dy;
      if (d2 < minD2) {
        minD2 = d2;
        target = it;
      }
    }
    const threshold2 = (CONSTANTS.drag?.threshold || 48) ** 2;

    if (target && minD2 < threshold2) {
      const canCombine = !!combinePair(elementKey, target.elementKey);
      if (canCombine) {
        setHighlightedIds([target.id]);
        setPaletteDrag({ key: elementKey, x: overlayX, y: overlayY, highlight: true });
      } else {
        setHighlightedIds([]);
        setPaletteDrag({ key: elementKey, x: overlayX, y: overlayY, highlight: false });
      }
    } else {
      setHighlightedIds([]);
      setPaletteDrag(null);
    }
  }, [gamePhase, instances, getBoardCoords]);

  const onBoardDragLeave = useCallback(() => {
    setHighlightedIds([]);
    setPaletteDrag(null);
  }, []);

  const onBoardDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (gamePhase !== 'normal') return;
    
    e.preventDefault();
    const incomingKey = e.dataTransfer.getData('text/plain');
    setHighlightedIds([]);
    setPaletteDrag(null);

    if (!incomingKey || !boardRef.current) return;

    const { x, y, rect } = getBoardCoords(e.clientX, e.clientY);
    const dropX = x - 32;
    const dropY = y - 32;

    if (tutorialState.active === 'first_combination' && incomingKey === 'stone') {
      if (tutorialState.step === 0 || tutorialState.step === 1) {
        advanceTutorial();
      }
    }

    let target: Instance | null = null;
    let minD2 = Infinity;
    for (const it of instances) {
      const dx = (it.x + 32) - x;
      const dy = (it.y + 32) - y;
      const d2 = dx * dx + dy * dy;
      if (d2 < minD2) {
        minD2 = d2;
        target = it;
      }
    }

    const threshold2 = (CONSTANTS.drag?.threshold || 48) ** 2;
    if (target && minD2 < threshold2) {
      const result = combinePair(incomingKey, target.elementKey);
      if (result) {
        playSoundEffect('combine_success');
        setInstances(prev => prev.filter(i => i.id !== target!.id));
        setEffects(prev => [...prev, { id: Date.now(), x: target!.x, y: target!.y, key: result }]);
      } else {
        playSoundEffect('combine_fail');
        const clampedX = Math.min(Math.max(dropX, 8), (rect?.width || dropX) - 72);
        const clampedY = Math.min(Math.max(dropY, 64), (rect?.height || dropY) - 72);
        addInstance(incomingKey, clampedX, clampedY);
      }
    } else {
      const clampedX = Math.min(Math.max(dropX, 8), (rect?.width || dropX) - 72);
      const clampedY = Math.min(Math.max(dropY, 64), (rect?.height || dropY) - 72);
      addInstance(incomingKey, clampedX, clampedY);
    }
  }, [gamePhase, getBoardCoords, instances, playSoundEffect, setInstances, setEffects, addInstance, tutorialState, advanceTutorial]);

  return {
    drag,
    overLibrary,
    highlightedIds,
    paletteDrag,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onBoardDragOver,
    onBoardDragLeave,
    onBoardDrop,
  };
};
